import { useRef, useState, useEffect } from 'react';
import {
  requestDocument,
  uploadDocumentFile,
  deleteDocumentFile,
  getDocumentFileLink,
  acceptDocument,
  rejectDocument,
  processDocument,
  updateDocumentItem,
} from '../../../api/prospectApi';
import './DocCollectionWorkspace.css';

const CATEGORY_LABELS = {
  KYC:         'KYC',
  MF:          'Mutual Funds',
  EQUITY:      'Equity',
  BONDS:       'Bonds',
  INSURANCE:   'Insurance',
  PF_NPS:      'PF / NPS',
  FD:          'Fixed Deposits',
  REAL_ESTATE: 'Real Estate',
  OTHER:       'Other Assets',
};

const CATEGORY_ORDER = ['KYC', 'MF', 'EQUITY', 'BONDS', 'INSURANCE', 'PF_NPS', 'FD', 'REAL_ESTATE', 'OTHER'];

const STATUS_ICON = {
  NOT_REQUESTED: { icon: '○', color: 'var(--color-text-muted)' },
  REQUESTED:     { icon: '◉', color: '#d97706' },
  REJECTED:      { icon: '✕', color: '#dc2626' },
  RECEIVED:      { icon: '◎', color: '#2563eb' },
  PROCESSED:     { icon: '●', color: '#16a34a' },
  NOT_APPLICABLE:{ icon: '—', color: 'var(--color-text-muted)' },
};

const STATUS_LABELS = {
  NOT_REQUESTED:  'Not Requested',
  REQUESTED:      'Requested',
  REJECTED:       'Rejected',
  RECEIVED:       'Accepted',
  PROCESSED:      'Processed',
  NOT_APPLICABLE: 'N/A',
};

const STATUS_CHIP_CLASS = {
  NOT_REQUESTED:  'dcw__status--not-requested',
  REQUESTED:      'dcw__status--requested',
  REJECTED:       'dcw__status--rejected',
  RECEIVED:       'dcw__status--received',
  PROCESSED:      'dcw__status--processed',
  NOT_APPLICABLE: 'dcw__status--na',
};

const TRAIL_CONFIG = {
  REQUESTED:    { label: 'Requested from client',    color: '#d97706' },
  RE_REQUESTED: { label: 'Re-requested from client', color: '#d97706' },
  UPLOADED:     { label: 'File uploaded',            color: '#2563eb' },
  REJECTED:     { label: 'Rejected',                 color: '#dc2626' },
  ACCEPTED:     { label: 'Accepted',                 color: '#2563eb' },
  PROCESSED:    { label: 'Processed',                color: '#16a34a' },
};

function formatDate(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fileIcon(fileName) {
  if (!fileName) return '📄';
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.xlsm')) return '📊';
  if (lower.endsWith('.docx') || lower.endsWith('.doc')) return '📝';
  if (lower.endsWith('.pptx') || lower.endsWith('.ppt')) return '📑';
  if (lower.endsWith('.pdf')) return '📕';
  return '📄';
}

function buildGroups(items) {
  const groups = {};
  for (let i = 0; i < CATEGORY_ORDER.length; i++) {
    groups[CATEGORY_ORDER[i]] = [];
  }
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (groups[item.category]) {
      groups[item.category].push(item);
    } else {
      if (!groups['OTHER']) groups['OTHER'] = [];
      groups['OTHER'].push(item);
    }
  }
  return groups;
}

export default function DocCollectionWorkspace({ prospectId, documentItems, readOnly, onJourneyUpdated }) {
  const fileInputRef = useRef(null);
  const [selectedDocKey, setSelectedDocKey] = useState(null);
  const [rejectingDocKey, setRejectingDocKey] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [loadingAction, setLoadingAction] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [navWidth, setNavWidth] = useState(500);
  const [detailView, setDetailView] = useState('files');
  const [navFilter, setNavFilter] = useState('applicable');

  const resizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);

  // Auto-select first doc on initial load
  useEffect(() => {
    if (!selectedDocKey && documentItems && documentItems.length > 0) {
      setSelectedDocKey(documentItems[0].doc_key);
    }
  }, []);

  // Drag-to-resize nav
  useEffect(() => {
    function onMouseMove(e) {
      if (!resizingRef.current) return;
      const delta = e.clientX - resizeStartXRef.current;
      const next = Math.max(200, Math.min(500, resizeStartWidthRef.current + delta));
      setNavWidth(next);
    }
    function onMouseUp() {
      resizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  function handleResizerMouseDown(e) {
    resizingRef.current = true;
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = navWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }

  const items = documentItems || [];
  const groups = buildGroups(items);

  // Derive currently selected doc from latest items prop
  let selectedDoc = null;
  for (let i = 0; i < items.length; i++) {
    if (items[i].doc_key === selectedDocKey) {
      selectedDoc = items[i];
      break;
    }
  }

  function handleDocSelect(docKey) {
    setSelectedDocKey(docKey);
    setRejectingDocKey(null);
    setRejectReason('');
    setUploadError('');
  }

  async function handleRequestDoc() {
    setLoadingAction('request');
    try {
      const updated = await requestDocument(prospectId, selectedDocKey);
      onJourneyUpdated(updated);
    } catch (ex) {
      alert('Failed: ' + ex.message);
    } finally {
      setLoadingAction(null);
    }
  }

  function handleUploadClick() {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }

  async function handleFileSelected(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const updated = await uploadDocumentFile(prospectId, selectedDocKey, file);
      onJourneyUpdated(updated);
    } catch (ex) {
      setUploadError(ex.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteFile(fileId, fileName) {
    if (!window.confirm('Delete "' + fileName + '"? This cannot be undone.')) return;
    try {
      const updated = await deleteDocumentFile(prospectId, selectedDocKey, fileId);
      onJourneyUpdated(updated);
    } catch (ex) {
      alert('Could not delete file: ' + (ex.message || 'Unknown error'));
    }
  }

  async function handleOpenFile(fileId) {
    try {
      const result = await getDocumentFileLink(prospectId, selectedDocKey, fileId);
      const link = result.protocol_link || result.protocolLink;
      if (link) window.open(link, '_self');
    } catch (ex) {
      alert('Could not open file: ' + (ex.message || 'Unknown error'));
    }
  }

  async function handleAcceptDoc() {
    setLoadingAction('accept');
    try {
      const updated = await acceptDocument(prospectId, selectedDocKey);
      onJourneyUpdated(updated);
    } catch (ex) {
      alert('Failed: ' + ex.message);
    } finally {
      setLoadingAction(null);
    }
  }

  function handleRejectClick() {
    setRejectingDocKey(selectedDocKey);
    setRejectReason('');
  }

  function handleRejectCancel() {
    setRejectingDocKey(null);
    setRejectReason('');
  }

  async function handleRejectConfirm() {
    if (!rejectReason.trim()) return;
    setLoadingAction('reject');
    try {
      const updated = await rejectDocument(prospectId, selectedDocKey, rejectReason.trim());
      onJourneyUpdated(updated);
      setRejectingDocKey(null);
      setRejectReason('');
    } catch (ex) {
      alert('Failed: ' + ex.message);
    } finally {
      setLoadingAction(null);
    }
  }

  async function handleReRequestDoc() {
    setLoadingAction('rerequest');
    try {
      const updated = await requestDocument(prospectId, selectedDocKey);
      onJourneyUpdated(updated);
    } catch (ex) {
      alert('Failed: ' + ex.message);
    } finally {
      setLoadingAction(null);
    }
  }

  async function handleProcessDoc() {
    setLoadingAction('process');
    try {
      const updated = await processDocument(prospectId, selectedDocKey);
      onJourneyUpdated(updated);
    } catch (ex) {
      alert('Failed: ' + ex.message);
    } finally {
      setLoadingAction(null);
    }
  }

  async function handleToggleNa(currentIsNa) {
    try {
      const updated = await updateDocumentItem(
        prospectId, selectedDocKey, null, null, !currentIsNa
      );
      onJourneyUpdated(updated);
    } catch (ex) {
      alert('Failed: ' + ex.message);
    }
  }

  function applyNavFilter(docItems) {
    if (navFilter === 'applicable') return docItems.filter(d => d.status !== 'NOT_APPLICABLE');
    if (navFilter === 'pending')    return docItems.filter(d => d.status === 'NOT_REQUESTED' || d.status === 'REQUESTED' || d.status === 'REJECTED');
    if (navFilter === 'done')       return docItems.filter(d => d.status === 'PROCESSED');
    return docItems;
  }

  function renderNavGroup(category) {
    const groupItems = applyNavFilter(groups[category] || []);
    if (groupItems.length === 0) return null;

    return (
      <div key={category} className="dcw__nav-group">
        <div className="dcw__nav-group-header">
          <span className="dcw__nav-group-label">{CATEGORY_LABELS[category] || category}</span>
        </div>
        {groupItems.map(function(doc) {
          const statusMeta = STATUS_ICON[doc.status] || STATUS_ICON.NOT_REQUESTED;
          const isSelected = selectedDocKey === doc.doc_key;
          return (
            <div
              key={doc.doc_key}
              className={'dcw__nav-doc-row' + (isSelected ? ' dcw__nav-doc-row--selected' : '')}
              onClick={() => handleDocSelect(doc.doc_key)}
            >
              <span className="dcw__nav-status-icon" style={{ color: statusMeta.color }}>
                {statusMeta.icon}
              </span>
              <span className="dcw__nav-doc-name">{doc.subcategory}</span>
              {doc.level > 0 && <span className="dcw__nav-level">L{doc.level}</span>}
            </div>
          );
        })}
      </div>
    );
  }

  function renderDetail() {
    if (!selectedDoc) {
      return (
        <div className="dcw__detail-empty">
          <div>Select a document from the list</div>
        </div>
      );
    }

    const doc = selectedDoc;
    const files = doc.files || [];
    const trail = doc.trail || [];
    const statusMeta = STATUS_ICON[doc.status] || STATUS_ICON.NOT_REQUESTED;
    const isRejecting = rejectingDocKey === doc.doc_key;

    const sortedTrail = trail.slice().sort(function(a, b) {
      return new Date(b.logged_at) - new Date(a.logged_at);
    });

    return (
      <div className="dcw__detail-inner">

        {/* Header */}
        <div className="dcw__detail-header">
          <div className="dcw__detail-title">{doc.subcategory}</div>
          <div className="dcw__detail-meta">
            <span className="dcw__detail-category">{CATEGORY_LABELS[doc.category] || doc.category}</span>
            {doc.level > 0 && <span className="dcw__detail-level-badge">L{doc.level}</span>}
            <span className={'dcw__status-chip ' + (STATUS_CHIP_CLASS[doc.status] || '')}>
              <span style={{ color: statusMeta.color }}>{statusMeta.icon}</span>
              {' '}{STATUS_LABELS[doc.status] || doc.status}
            </span>
          </div>
        </div>

        {/* Action bar */}
        {!readOnly && doc.status !== 'NOT_APPLICABLE' && (
          <div className="dcw__action-bar">
            {doc.status === 'NOT_REQUESTED' && (
              <button
                className="dcw__action-btn dcw__action-btn--primary"
                onClick={handleRequestDoc}
                disabled={loadingAction === 'request'}
              >
                {loadingAction === 'request' ? '...' : '📤 Mark as Requested'}
              </button>
            )}

            {(doc.status === 'REQUESTED' || doc.status === 'REJECTED') && (
              <button
                className="dcw__action-btn dcw__action-btn--upload"
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : '+ Upload File'}
              </button>
            )}

            {doc.status === 'REJECTED' && (
              <button
                className="dcw__action-btn"
                onClick={handleReRequestDoc}
                disabled={loadingAction === 'rerequest'}
              >
                {loadingAction === 'rerequest' ? '...' : '⟳ Re-request'}
              </button>
            )}

            {(doc.status === 'REQUESTED' || doc.status === 'REJECTED') && files.length > 0 && (
              <button
                className="dcw__action-btn dcw__action-btn--accept"
                onClick={handleAcceptDoc}
                disabled={loadingAction === 'accept'}
              >
                {loadingAction === 'accept' ? '...' : '✓ Accept'}
              </button>
            )}

            {(doc.status === 'REQUESTED' || doc.status === 'REJECTED') && files.length > 0 && (
              <button
                className="dcw__action-btn dcw__action-btn--reject"
                onClick={handleRejectClick}
              >
                ✕ Reject
              </button>
            )}

            {doc.status === 'RECEIVED' && (
              <>
                <button
                  className="dcw__action-btn dcw__action-btn--primary"
                  onClick={handleProcessDoc}
                  disabled={loadingAction === 'process'}
                >
                  {loadingAction === 'process' ? '...' : '✓ Mark as Processed'}
                </button>
                <button
                  className="dcw__action-btn dcw__action-btn--reject"
                  onClick={handleRejectClick}
                >
                  ✕ Reject
                </button>
              </>
            )}

            <button
              className={'dcw__action-btn dcw__action-btn--na' + (doc.is_not_applicable ? ' dcw__action-btn--na-active' : '')}
              onClick={() => handleToggleNa(doc.is_not_applicable)}
              title="Mark as Not Applicable"
            >
              N/A
            </button>
          </div>
        )}

        {/* Reject form — inline, above columns */}
        {isRejecting && (
          <div className="dcw__reject-form">
            <textarea
              className="dcw__reject-textarea"
              placeholder="Reason for rejection (required)..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={2}
              autoFocus
            />
            <div className="dcw__reject-actions">
              <button
                className="dcw__action-btn dcw__action-btn--reject"
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim() || loadingAction === 'reject'}
              >
                {loadingAction === 'reject' ? '...' : 'Confirm Reject'}
              </button>
              <button className="dcw__action-btn" onClick={handleRejectCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* View toggle */}
        <div className="dcw__view-toggle">
          <button className={'dcw__view-btn' + (detailView === 'files' ? ' dcw__view-btn--active' : '')} onClick={() => setDetailView('files')}>Files</button>
          <button className={'dcw__view-btn' + (detailView === 'trail' ? ' dcw__view-btn--active' : '')} onClick={() => setDetailView('trail')}>Trail</button>
          <button className={'dcw__view-btn' + (detailView === 'both'  ? ' dcw__view-btn--active' : '')} onClick={() => setDetailView('both')}>Both</button>
        </div>

        {/* Body columns */}
        <div className="dcw__body-cols">

          {/* Files column */}
          {(detailView === 'files' || detailView === 'both') && (
          <div className={'dcw__col-files' + (detailView !== 'both' ? ' dcw__col--full' : '')}>
            <div className="dcw__section-label">📎 Files</div>
            {uploadError && <div className="dcw__upload-error">{uploadError}</div>}
            <input
              ref={fileInputRef}
              type="file"
              className="dcw__file-input"
              onChange={handleFileSelected}
              accept="*/*"
            />
            {files.length === 0 ? (
              <div className="dcw__files-empty">No files uploaded yet.</div>
            ) : (
              <div className="dcw__file-list">
                {files.map(function(f) {
                  return (
                    <div key={f.file_id} className="dcw__file-row">
                      <span className="dcw__file-icon">{fileIcon(f.file_name)}</span>
                      <div className="dcw__file-info">
                        <span className="dcw__file-name">{f.file_name}</span>
                        <span className="dcw__file-meta">
                          {formatDate(f.uploaded_at)} · {formatFileSize(f.file_size_bytes)}
                        </span>
                      </div>
                      <div className="dcw__file-actions">
                        <button
                          className="dcw__file-open-btn"
                          onClick={() => handleOpenFile(f.file_id)}
                          title="Open in desktop app"
                        >
                          Open
                        </button>
                        {!readOnly && (
                          <button
                            className="dcw__file-delete-btn"
                            onClick={() => handleDeleteFile(f.file_id, f.file_name)}
                            title="Delete file"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* Trail column */}
          {(detailView === 'trail' || detailView === 'both') && (
          <div className={'dcw__col-trail' + (detailView !== 'both' ? ' dcw__col--full' : '')}>
            <div className="dcw__section-label">🕐 Trail</div>
            {sortedTrail.length === 0 ? (
              <div className="dcw__trail-empty">No activity yet.</div>
            ) : (
              <div className="dcw__trail-list">
                {sortedTrail.map(function(entry) {
                  const config = TRAIL_CONFIG[entry.event] || { label: entry.event, color: 'var(--color-text-muted)' };
                  return (
                    <div key={entry.entry_id} className="dcw__trail-entry">
                      <span className="dcw__trail-dot" style={{ background: config.color }} />
                      <div className="dcw__trail-content">
                        <span className="dcw__trail-event" style={{ color: config.color }}>
                          {config.label}
                        </span>
                        <span className="dcw__trail-date">{formatDate(entry.logged_at)}</span>
                        {entry.notes && entry.event !== 'UPLOADED' && (
                          <div className="dcw__trail-notes">"{entry.notes}"</div>
                        )}
                        {entry.notes && entry.event === 'UPLOADED' && (
                          <div className="dcw__trail-filename">{entry.notes}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

        </div>

      </div>
    );
  }

  return (
    <div className="dcw__workspace">
      {/* Left nav */}
      <div className="dcw__nav" style={{ width: navWidth }}>
        <div className="dcw__nav-filter">
          {[
            { key: 'all',        label: 'All' },
            { key: 'applicable', label: 'Applicable' },
            { key: 'pending',    label: 'Pending' },
            { key: 'done',       label: 'Done' },
          ].map(function(f) {
            return (
              <button
                key={f.key}
                className={'dcw__nav-filter-btn' + (navFilter === f.key ? ' dcw__nav-filter-btn--active' : '')}
                onClick={() => setNavFilter(f.key)}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        {CATEGORY_ORDER.map(cat => renderNavGroup(cat))}
      </div>

      {/* Drag handle */}
      <div className="dcw__nav-resizer" onMouseDown={handleResizerMouseDown} />

      {/* Right detail panel */}
      <div className="dcw__detail">
        {renderDetail()}
      </div>
    </div>
  );
}
